/**
 * TraderProfile — deep-link page for /whos-on-top/trader/:traderId
 *
 * Shows trader stats and an optional copy-trading panel when ?action=copy.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";
import { toast } from "sonner";
import { logURLEvent } from "@/lib/logURLEvent";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    ArrowLeft, Trophy, Copy, TrendingUp, TrendingDown,
    Users, Target, Loader2,
} from "lucide-react";

export default function TraderProfile() {
    const { traderId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [trader, setTrader] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copyBusy, setCopyBusy] = useState(false);

    const showCopyPanel = searchParams.get("action") === "copy";

    useEffect(() => {
        logURLEvent("NAVIGATION_DEEP_LINK", `/whos-on-top/trader/${traderId}`, {
            traderId: traderId || "",
            action: searchParams.get("action") || "",
        });
    }, [traderId, searchParams]);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get(`/leaderboard/trader/${traderId}`);
                setTrader(res.data);
            } catch {
                // Try to find in leaderboard
                try {
                    const lb = await api.get("/leaderboard");
                    const rows = lb.data?.rows || lb.data?.rankings || [];
                    const found = rows.find((r) => r.user_id === traderId || r.id === traderId);
                    setTrader(found || { user_id: traderId, display_name: `Trader ${traderId?.slice(0, 8)}` });
                } catch {
                    setTrader({ user_id: traderId, display_name: `Trader ${traderId?.slice(0, 8)}` });
                }
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [traderId]);

    const toggleCopyPanel = () => {
        const next = new URLSearchParams(searchParams);
        if (showCopyPanel) {
            next.delete("action");
        } else {
            next.set("action", "copy");
        }
        setSearchParams(next, { replace: false });
    };

    const startCopy = async () => {
        setCopyBusy(true);
        try {
            await api.post("/copy-trading/start", { target_id: traderId });
            toast.success("Now copying this trader");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Copy failed");
        } finally {
            setCopyBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading trader...
            </div>
        );
    }

    const pnl = Number(trader?.pnl_pct || 0);
    const winRate = Number(trader?.win_rate || 0);
    const trades = trader?.total_trades || 0;

    return (
        <div className="p-6" data-testid="trader-profile-page">
            <div className="flex items-center gap-3 mb-6">
                <Button variant="ghost" size="sm" onClick={() => navigate("/app/whos-on-top")} data-testid="trader-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <div className="overline text-primary flex items-center gap-2">
                    <Trophy className="w-4 h-4" /> Trader Profile
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main profile card */}
                <Card className="lg:col-span-2" data-testid="trader-stats-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                                {(trader?.display_name || "?")[0].toUpperCase()}
                            </div>
                            <div>
                                <div className="text-xl">{trader?.display_name || trader?.name || "Trader"}</div>
                                <div className="text-sm text-muted-foreground font-normal">
                                    {trader?.tier || "Individual"} · ID: {traderId?.slice(0, 12)}
                                </div>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div className="border border-border rounded-lg p-3 text-center">
                                <div className="text-xs text-muted-foreground mb-1">P&L</div>
                                <div className={`text-2xl font-mono font-bold ${toneClassName(signTone(pnl))}`}>
                                    {signedPrefix(pnl)}{pnl.toFixed(2)}%
                                </div>
                            </div>
                            <div className="border border-border rounded-lg p-3 text-center">
                                <div className="text-xs text-muted-foreground mb-1">Win Rate</div>
                                <div className="text-2xl font-mono font-bold">{winRate.toFixed(0)}%</div>
                            </div>
                            <div className="border border-border rounded-lg p-3 text-center">
                                <div className="text-xs text-muted-foreground mb-1">Total Trades</div>
                                <div className="text-2xl font-mono font-bold">{trades}</div>
                            </div>
                            <div className="border border-border rounded-lg p-3 text-center">
                                <div className="text-xs text-muted-foreground mb-1">Copiers</div>
                                <div className="text-2xl font-mono font-bold">{trader?.copiers || 0}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Copy action card */}
                <Card data-testid="trader-copy-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Copy className="w-5 h-5 text-primary" />
                            Copy Trading
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-xs text-muted-foreground">
                            Mirror this trader's positions automatically. Your allocations will follow their strategy.
                        </p>
                        <div className="flex items-center gap-2 text-xs">
                            {pnl >= 0 ? (
                                <TrendingUp className="w-4 h-4 text-emerald-500" />
                            ) : (
                                <TrendingDown className="w-4 h-4 text-rose-500" />
                            )}
                            <span>{pnl >= 0 ? "Positive" : "Negative"} returns over period</span>
                        </div>
                        <Button
                            className="w-full"
                            onClick={showCopyPanel ? startCopy : toggleCopyPanel}
                            disabled={copyBusy}
                            data-testid="trader-copy-btn"
                        >
                            {copyBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                            {showCopyPanel ? "Confirm — Start Copying" : "Copy This Trader"}
                        </Button>
                        {showCopyPanel && (
                            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={toggleCopyPanel}>
                                Cancel
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
